import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCalendarAlt,
  FaCalendarPlus,
  FaAward,
  FaChartLine,
  FaRocket,
  FaTimes,
  FaQuoteLeft,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import "./Announcement.css";

/**
 * Default timezone offset for India Standard Time (IST).
 * When env timestamps do not specify a timezone (e.g. 20260829T22:30:00:000),
 * this ensures all users globally synchronize to the exact same instant in IST.
 */
const DEFAULT_TIMEZONE_OFFSET = "+05:30";

/**
 * Parses multiple date formats safely (ISO, YYYY-MM-DD, compact YYYYMMDDTHH:MM:SS:000, etc.)
 * Anchors timestamps without an explicit timezone to IST (+05:30) so release times
 * are universally synchronized worldwide regardless of the student's device timezone.
 */
export const parseFlexibleDate = (dateStr, defaultOffset = DEFAULT_TIMEZONE_OFFSET) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const raw = dateStr.trim();
  if (!raw) return null;

  // Check if string already contains a timezone indicator (Z or +HH:MM / -HH:MM / +HHMM)
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);

  // 1. Compact format: 20260829T22:30:00:000 or 20260829T22:30:00 or 20260829
  const compactMatch = raw.match(
    /^(\d{4})(\d{2})(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?::(\d{3}))?)?(.*)$/
  );
  if (compactMatch) {
    const [, y, m, day, hr = "00", min = "00", sec = "00", ms = "000", tz = ""] = compactMatch;
    const finalTz = tz ? (tz.startsWith(":") ? tz.slice(1) : tz) : defaultOffset;
    const isoString = `${y}-${m}-${day}T${hr}:${min}:${sec}.${ms}${finalTz}`;
    const parsed = new Date(isoString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // 2. Standard ISO with Time: e.g. 2026-08-29T22:30:00
  if (raw.includes("T")) {
    const isoString = hasTz ? raw : `${raw}${defaultOffset}`;
    const parsed = new Date(isoString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // 3. Date-only: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = new Date(`${raw}T00:00:00.000${defaultOffset}`);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // 4. Standard Date fallback
  const fallback = new Date(raw);
  if (!isNaN(fallback.getTime())) {
    return fallback;
  }

  return null;
};

/**
 * Validates if the current time is within the configured start and end date range.
 * Supports REACT_POPUP_MESSAGE_START_DATE and REACT_POPUP_MESSAGE_END_DATE
 * (and REACT_APP_ prefixed equivalents for Create React App).
 */
export const isPopupDateActive = () => {
  const startRaw = (
    process.env.REACT_APP_POPUP_MESSAGE_START_DATE ||
    process.env.REACT_POPUP_MESSAGE_START_DATE ||
    ""
  ).trim();

  const endRaw = (
    process.env.REACT_APP_POPUP_MESSAGE_END_DATE ||
    process.env.REACT_POPUP_MESSAGE_END_DATE ||
    ""
  ).trim();

  // If neither date is specified, default to inactive
  if (!startRaw && !endRaw) {
    return false;
  }

  const now = new Date();

  // Validate start date (inclusive)
  if (startRaw) {
    const startDate = parseFlexibleDate(startRaw);
    if (startDate && now < startDate) {
      return false;
    }
  }

  // Validate end date (inclusive)
  if (endRaw) {
    let endDate = parseFlexibleDate(endRaw);
    if (endDate) {
      // If end date is YYYY-MM-DD or YYYYMMDD without time, include whole day in IST
      if (!endRaw.includes("T") && !endRaw.includes(":")) {
        endDate = new Date(
          endDate.getTime() + (23 * 3600 + 59 * 60 + 59) * 1000 + 999
        );
      }
      if (now > endDate) {
        return false;
      }
    }
  }

  return true;
};

export default function Phase1ResultsPopup({
  forceOpen = false,
  onCloseCallback = null,
}) {
  const isDateEligible = useMemo(() => isPopupDateActive(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);

  // Target announcement date: 4th September 2026 00:00:00
  const targetDate = useMemo(() => {
    return new Date("2026-09-04T00:00:00");
  }, []);

  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isPast: false,
  });

  // Calculate remaining time
  const calculateTimeLeft = useCallback(() => {
    const difference = targetDate.getTime() - new Date().getTime();
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      isPast: false,
    };
  }, [targetDate]);

  // Update countdown every second
  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeLeft]);

  // Handle initial popup display based on date eligibility and dismissal timestamp
  useEffect(() => {
    if (!isDateEligible && !forceOpen) return;

    if (forceOpen) {
      setIsOpen(true);
      return;
    }

    const dismissedAt = localStorage.getItem("grademe_phase1_popup_dismissed_at");
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const hoursSinceDismissal = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissal < 24) {
        // Dismissed within last 24h -> keep modal closed initially, show floating badge
        return;
      }
    }

    // Auto-open with smooth entrance delay
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 850);

    return () => clearTimeout(timer);
  }, [isDateEligible, forceOpen]);

  // Close handler with optional 24-hour suppression
  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    if (dontShowToday) {
      localStorage.setItem(
        "grademe_phase1_popup_dismissed_at",
        new Date().toISOString()
      );
    }
    if (onCloseCallback) onCloseCallback();
  };

  // Quick re-open from floating trigger
  const handleOpen = () => {
    setIsOpen(true);
  };

  // Generate Google Calendar Link
  const handleAddGoogleCalendar = () => {
    const title = encodeURIComponent("GradeMe AI - Phase 1 Results Declaration");
    const details = encodeURIComponent(
      "Your Phase 1 results will be declared on 4th September. Please visit the GradeMe AI portal to know your qualification status and download your Participation Certificate.\n\nPortal: " +
        window.location.origin
    );
    const location = encodeURIComponent("GradeMe AI Student Portal");
    const gCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=20260904T033000Z/20260904T123000Z&details=${details}&location=${location}`;
    window.open(gCalUrl, "_blank", "noopener,noreferrer");
  };

  // If outside active date range and not forced open, render nothing
  if (!isDateEligible && !forceOpen) {
    return null;
  }

  return (
    <>
      {/* Floating Interactive Trigger Pill (Shown when modal is closed) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            className="p1-floating-trigger-container"
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 30 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <motion.button
              className="p1-floating-pill"
              onClick={handleOpen}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              aria-label="View Phase 1 Results Announcement"
            >
              <span className="p1-floating-pulse-ring"></span>
              <span className="p1-floating-icon">
                <FaAward />
              </span>
              <div className="p1-floating-text">
                <span className="p1-floating-title">Phase 1 Results</span>
                <span className="p1-floating-sub">Declaring 4th Sept</span>
              </div>
              <span className="p1-floating-badge">
                <HiSparkles /> New
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Animated Modal Overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="p1-modal-backdrop-wrapper">
            <motion.div
              className="p1-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            />

            <div className="p1-modal-center-container">
              <motion.div
                className="p1-modal-card"
                initial={{ opacity: 0, scale: 0.88, y: 40 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: { type: "spring", stiffness: 320, damping: 28 },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.88,
                  y: 40,
                  transition: { duration: 0.22 },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Ambient Decorative Background Glows */}
                <div className="p1-glow-top-left"></div>
                <div className="p1-glow-bottom-right"></div>
                <div className="p1-particles-overlay">
                  <span className="p1-particle p1-p1">✨</span>
                  <span className="p1-particle p1-p2">⭐</span>
                  <span className="p1-particle p1-p3">🎉</span>
                  <span className="p1-particle p1-p4">🏆</span>
                </div>

                {/* Modal Close Button */}
                <motion.button
                  className="p1-close-btn"
                  onClick={handleClose}
                  whileHover={{ scale: 1.15, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Close Announcement"
                >
                  <FaTimes />
                </motion.button>

                {/* Live Countdown Section */}
                <div className="p1-countdown-section">
                  <div className="p1-countdown-header">
                    <FaCalendarAlt className="p1-cal-icon" />
                    <span>Phase 1 Results Declaration Date: <strong>4th September</strong></span>
                  </div>

                  {!timeLeft.isPast ? (
                    <div className="p1-countdown-grid">
                      <div className="p1-countdown-box">
                        <span className="p1-cd-value">
                          {String(timeLeft.days).padStart(2, "0")}
                        </span>
                        <span className="p1-cd-label">Days Left</span>
                      </div>
                    </div>
                  ) : (
                    <div className="p1-live-now-badge">
                      <span className="p1-live-dot"></span>
                      <span>🎉 Results & Certificates are Now Declared!</span>
                    </div>
                  )}
                </div>

                {/* Core User Message Banner (Exact Wording) */}
                <motion.div
                  className="p1-message-banner-box"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                >
                  <div className="p1-msg-content-wrapper">
                    <div className="p1-msg-quote-icon" aria-hidden="true">
                      <FaQuoteLeft />
                    </div>
                    <div className="p1-msg-paragraphs">
                      <p className="p1-msg-p1">
                        <strong>Your Phase 1 results will be declared on 4th September.</strong>
                      </p>
                      <p className="p1-msg-p2">
                        Please visit <strong>GradeMe AI</strong> to know your qualification status and download your <strong>Participation Certificate</strong>.
                      </p>
                      <p className="p1-msg-p3">
                        We wish you the very best! <em>Keep learning, keep improving, and keep achieving!</em> 🌟
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Interactive Key Highlights Row */}
                <div className="p1-highlights-row">
                  <motion.div
                    className="p1-highlight-card"
                    whileHover={{ y: -3, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 350 }}
                  >
                    <div className="p1-hl-icon-wrap p1-hl-icon-gold">
                      <FaAward />
                    </div>
                    <div className="p1-hl-content">
                      <h4>Participation Certificate</h4>
                      <p>Instant verifiable certificate download available upon declaration.</p>
                    </div>
                  </motion.div>

                  <motion.div
                    className="p1-highlight-card"
                    whileHover={{ y: -3, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 350 }}
                  >
                    <div className="p1-hl-icon-wrap p1-hl-icon-blue">
                      <FaChartLine />
                    </div>
                    <div className="p1-hl-content">
                      <h4>Qualification Status</h4>
                      <p>Find out if you're moving to Phase 2.</p>
                    </div>
                  </motion.div>

                  <motion.div
                    className="p1-highlight-card"
                    whileHover={{ y: -3, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 350 }}
                  >
                    <div className="p1-hl-icon-wrap p1-hl-icon-purple">
                      <FaRocket />
                    </div>
                    <div className="p1-hl-content">
                      <h4>Next Round Pathways</h4>
                      <p>Roadmap for Phase 2 (Only for qualified students).</p>
                    </div>
                  </motion.div>
                </div>

                {/* Action Controls & Calendar Reminders */}
                <div className="p1-action-footer">
                  <div className="p1-calendar-actions">
                    <motion.button
                      className="p1-btn-cal p1-btn-gcal"
                      onClick={handleAddGoogleCalendar}
                      whileHover={{ scale: 1.03, y: -1 }}
                      whileTap={{ scale: 0.97 }}
                      title="Add reminder to Google Calendar"
                    >
                      <FaCalendarPlus />
                      <span>Add to Google Calendar</span>
                    </motion.button>
                  </div>

                  <div className="p1-dismiss-row">
                    <label className="p1-checkbox-label">
                      <input
                        type="checkbox"
                        checked={dontShowToday}
                        onChange={(e) => setDontShowToday(e.target.checked)}
                      />
                      <span>Don't auto-popup again today</span>
                    </label>

                    <motion.button
                      className="p1-btn-primary"
                      onClick={handleClose}
                      whileHover={{ scale: 1.03, y: -1 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <span>Got It, Thank You!</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
